import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useFeedback } from '@/app/feedback';
import {
  Breadcrumbs,
  Container,
  ErrorState,
  LoadingState,
  ResponsiveImage,
  Section,
  SectionHeading,
} from '@/components/common';
import { productDetailRoute, routes } from '@/config/routes';
import {
  mapPublicProductDetailToProductDetailViewModel,
  mapPublicProductSummaryToProductCardModel,
} from '@/features/catalog/api/catalogMappers';
import type { CatalogError } from '@/features/catalog/api/types';
import { ProductGrid } from '@/features/catalog/components';
import { useCatalog } from '@/features/catalog/context';
import { presentationForCategory } from '@/features/catalog/data/categoryPresentation';
import { useCategoryProducts, usePublicProduct } from '@/features/catalog/hooks';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

import { ProductNotFoundPage } from './ProductNotFoundPage';

const badgeLabels = {
  bestseller: 'Bestseller',
  new: 'New',
  popular: 'Popular',
  seasonal: 'Seasonal',
} as const;

export function ProductDetailPage() {
  const { categorySlug, productSlug } = useParams();
  const { media } = useCatalog();
  const productQuery = usePublicProduct(productSlug);
  const product = productQuery.data
    ? mapPublicProductDetailToProductDetailViewModel(productQuery.data, media)
    : undefined;
  const relatedQuery = useCategoryProducts(product?.category, { limit: 4, sort: 'featured' });
  const related = useMemo(
    () =>
      (relatedQuery.data?.pages ?? [])
        .flatMap((page) => page.items)
        .filter((item) => item.id !== product?.id && item.categorySlug === product?.category)
        .slice(0, 3)
        .map((item) => mapPublicProductSummaryToProductCardModel(item, media)),
    [media, product?.category, product?.id, relatedQuery.data],
  );
  const [quantity, setQuantity] = useState<number | null>(1);
  const { showInfo } = useFeedback();
  const presentation = product
    ? presentationForCategory(product.category, product.category)
    : undefined;
  useDocumentMetadata(
    product?.seo.title ?? product?.name ?? 'Product',
    product?.seo.description ?? product?.shortDescription,
  );

  if (productQuery.isPending) {
    return (
      <Section spacing="large">
        <Container className="product-detail" size="wide" aria-busy="true">
          <LoadingState label="Loading product details" />
        </Container>
      </Section>
    );
  }
  if ((productQuery.error as CatalogError | null)?.code === 'product-not-found')
    return <ProductNotFoundPage />;
  if (productQuery.isError || !product || !presentation) {
    return (
      <Section spacing="large">
        <Container>
          <ErrorState
            title="This product could not be loaded"
            description="The product may still be available. Please retry the catalog connection."
            onRetry={() => void productQuery.refetch()}
          />
        </Container>
      </Section>
    );
  }
  if (categorySlug !== product.category) {
    return <Navigate replace to={productDetailRoute(product.category, product.slug)} />;
  }

  const primaryMedia = product.media[0] ?? {
    id: 'placeholder',
    url: product.imageUrl,
    alt: product.imageAlt,
  };

  return (
    <>
      <Section spacing="compact">
        <Container size="wide">
          <Breadcrumbs
            items={[
              { label: 'Home', to: routes.home },
              { label: 'Products', to: routes.products },
              { label: presentation.shortTitle, to: presentation.route },
              { label: product.name },
            ]}
          />
        </Container>
      </Section>
      <Section spacing="compact">
        <Container className="product-detail" size="wide">
          <div className="product-detail__media">
            <ResponsiveImage
              src={primaryMedia.url}
              alt={primaryMedia.alt}
              width={primaryMedia.width ?? 800}
              height={primaryMedia.height ?? 640}
              loading="eager"
            />
            {product.media.length > 1 && (
              <div className="product-media-thumbnails" aria-label="Product media gallery">
                {product.media.slice(1).map((item) => (
                  <ResponsiveImage
                    key={item.id}
                    src={item.url}
                    alt={item.alt}
                    width={item.width ?? 200}
                    height={item.height ?? 160}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="product-detail__content">
            <p className="eyebrow">{presentation.shortTitle}</p>
            <h1>{product.name}</h1>
            <div className="product-detail__badges">
              {product.badges.map((badge) => (
                <Tag value={badgeLabels[badge]} key={badge} />
              ))}
              {product.customizable && <Tag severity="success" value="Customizable" />}
            </div>
            <p className="product-detail__price">
              <small>Starting at</small>
              {product.startingPriceLabel.replace(/^From /, '')}
              {product.compareAtPriceLabel && <del>{product.compareAtPriceLabel}</del>}
            </p>
            <p className="lead product-detail__description">{product.description}</p>
            <div className="detail-summary">
              <h2>Your personalization</h2>
              {product.customizationSummary.length > 0 ? (
                <ul>
                  {product.customizationSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>No personalization fields are required for this product.</p>
              )}
            </div>
            {product.options.map((option) => (
              <div className="product-option-summary" key={option.id}>
                <h3>{option.label}</h3>
                <p>{option.values.map((value) => value.label).join(' · ')}</p>
              </div>
            ))}
            <label className="quantity-field" htmlFor="product-quantity">
              Quantity <span>Placeholder only</span>
            </label>
            <InputNumber
              inputId="product-quantity"
              value={quantity}
              min={1}
              max={99}
              showButtons
              onValueChange={(event) => setQuantity(event.value ?? 1)}
            />
            <div className="product-detail__actions">
              <Button
                label="Customize This Product"
                icon="pi pi-sparkles"
                disabled={!product.customizable}
                onClick={() =>
                  showInfo(
                    'Customizer coming soon',
                    `The ${presentation.shortTitle.toLowerCase()} customizer is planned for a later milestone.`,
                  )
                }
              />
              <Button
                label="Add to Cart"
                icon="pi pi-shopping-bag"
                severity="secondary"
                outlined
                onClick={() =>
                  showInfo(
                    'Cart preview only',
                    'Cart state and checkout are planned for M7. Nothing was added yet.',
                  )
                }
              />
            </div>
          </div>
        </Container>
      </Section>
      <Section spacing="large">
        <Container size="wide">
          <SectionHeading
            eyebrow="More in this collection"
            title={`Explore ${presentation.shortTitle.toLowerCase()}`}
          />
          {related.length > 0 && <ProductGrid products={related} />}
          {relatedQuery.isError && (
            <p className="catalog-related-note" role="status">
              Related products are temporarily unavailable.
            </p>
          )}
        </Container>
      </Section>
    </>
  );
}
